import { useEffect, useState } from "react";

import { BottomSheet } from "../BottomSheet";
import { Button } from "../Button";
import { ChevronRightIcon } from "../Icons";
import { RadioDot, RadioGroupV2 } from "../RadioGroup/RadioGroupV2";
import { Typo } from "../Typo";
import { MobileDateRangeBottomSheet } from "./MobileDateRangeBottomSheet";

export type DateRange = { from: Date; to: Date };

export type DateRangePreset = {
  /** Stable key used for radio selection (e.g. "last30"). */
  key: string;
  /** User-facing, already-translated label (e.g. "Last 30 days"). */
  label: string;
  range: DateRange;
};

/** Sentinel radio key for the "Custom date range" row. */
const CUSTOM_KEY = "custom";

type MobileDateRangeSelectorLabels = {
  /** Shortcut sheet title, e.g. "Date range". */
  title: string;
  /** Custom row label, e.g. "Custom date range". */
  customDateRange: string;
  description?: string;
  /** Shortcut-sheet footer cancel button. */
  cancel: string;
  /** Shortcut-sheet footer apply button. */
  apply: string;
};

type MobileDateRangeSelectorProps = {
  isOpen: boolean;
  onClose: () => void;
  /** Commits the chosen range and (by convention) the parent closes the sheet. */
  onApply: (range: DateRange) => void;
  /** The currently applied range, used to pre-select on open. */
  value: DateRange | null;
  /** Preset options, labels supplied by the caller so i18n stays with it. */
  presets: DateRangePreset[];
  minDate?: Date;
  maxDate?: Date;
  limitDays?: number;
  labels: MobileDateRangeSelectorLabels;
};

const isSameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const isSameRange = (a: DateRange, b: DateRange) =>
  isSameDay(a.from, b.from) && isSameDay(a.to, b.to);

const formatRange = (range: DateRange, locale?: string) => {
  const fmt = (d: Date) =>
    d.toLocaleDateString(locale, {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  return `${fmt(range.from)} – ${fmt(range.to)}`;
};

/**
 * Two-step mobile date-range picker (finalized PART-3345 design, Jira 623938):
 *
 * 1. **Shortcut sheet** — presets as a single-select `RadioGroupV2`, plus a
 *    "Custom date range" navigation row (radio + trailing chevron, showing the
 *    selected range beneath the label once a custom range is set). Footer:
 *    Cancel / Apply.
 * 2. **Custom step** — reuses `MobileDateRangeBottomSheet` (calendar + wheel
 *    picker, 18-month floor / auto-swap / future-disabled rules), pushed in
 *    with a back arrow that returns to the shortcut sheet.
 *
 * Composition only — no new sheet primitive. Kept generic (caller supplies
 * presets + labels) so other mobile report pages can adopt it.
 */
export const MobileDateRangeSelector = ({
  isOpen,
  onClose,
  onApply,
  value,
  presets,
  minDate,
  maxDate,
  limitDays,
  labels,
}: MobileDateRangeSelectorProps) => {
  const [step, setStep] = useState<"shortcut" | "custom">("shortcut");
  // Draft radio selection: a preset key or CUSTOM_KEY.
  const [selectedKey, setSelectedKey] = useState<string>(CUSTOM_KEY);
  // Draft custom range chosen in the custom step (before the shortcut Apply).
  const [customRange, setCustomRange] = useState<DateRange | null>(null);

  // On open, restore the applied range: match it against a preset, else fall
  // back to the custom row (showing the applied range beneath it).
  useEffect(() => {
    if (!isOpen) return;
    setStep("shortcut");
    const matched = value
      ? presets.find((preset) => isSameRange(preset.range, value))
      : undefined;
    if (matched) {
      setSelectedKey(matched.key);
      setCustomRange(null);
    } else {
      setSelectedKey(value ? CUSTOM_KEY : (presets[0]?.key ?? CUSTOM_KEY));
      setCustomRange(value ?? null);
    }
    // Only re-run when the sheet transitions open — presets/value are read as a
    // snapshot at open time and shouldn't reset the draft mid-interaction.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const isCustomSelected = selectedKey === CUSTOM_KEY;
  const canApply = !isCustomSelected || customRange !== null;

  const handleApply = () => {
    if (isCustomSelected) {
      if (customRange) onApply(customRange);
      return;
    }
    const preset = presets.find((p) => p.key === selectedKey);
    if (preset) onApply(preset.range);
  };

  return (
    <>
      <BottomSheet
        isOpen={isOpen && step === "shortcut"}
        onClose={onClose}
        isDraggable
        title={labels.title}
        footer={
          <div className="flex gap-3">
            <Button
              variant="outline"
              size="md"
              className="flex-1"
              onClick={onClose}
            >
              {labels.cancel}
            </Button>
            <Button
              variant="brand"
              size="md"
              className="flex-1"
              onClick={handleApply}
              disabled={!canApply}
            >
              {labels.apply}
            </Button>
          </div>
        }
      >
        <div className="flex flex-col gap-3">
          {labels.description && (
            <Typo element="p" type="bodyXS" textColor="text-text-tertiary">
              {labels.description}
            </Typo>
          )}
          <RadioGroupV2
            value={isCustomSelected ? null : selectedKey}
            onChange={(key) => {
              setSelectedKey(key);
            }}
            options={presets.map((preset) => ({
              label: preset.label,
              value: preset.key,
            }))}
          />

          {/* Custom row: a navigation button (drills into the custom step).
              The RadioDot only provides visual continuity with the preset
              group above — this is a plain button, not an a11y radio control
              (no role="radio"/aria-checked). */}
          <button
            type="button"
            onClick={() => setStep("custom")}
            className="flex items-center gap-3 text-start"
          >
            <RadioDot checked={isCustomSelected} />
            <span className="flex min-w-0 flex-1 flex-col">
              <Typo element="span" type="bodySM" textColor="text-text-primary">
                {labels.customDateRange}
              </Typo>
              {isCustomSelected && customRange && (
                <Typo
                  element="span"
                  type="bodyXS"
                  textColor="text-text-tertiary"
                >
                  {formatRange(customRange)}
                </Typo>
              )}
            </span>
            <ChevronRightIcon
              className="h-4 w-4 shrink-0 text-icon-secondary"
              aria-hidden="true"
              focusable="false"
            />
          </button>
        </div>
      </BottomSheet>

      {/* Custom step — reuses the shared picker sheet, pushed in with a back
          arrow that returns to the shortcut sheet rather than closing. */}
      <MobileDateRangeBottomSheet
        isOpen={isOpen && step === "custom"}
        onClose={onClose}
        onBack={() => setStep("shortcut")}
        isDraggable
        onApply={(range) => {
          setCustomRange(range);
          setSelectedKey(CUSTOM_KEY);
          setStep("shortcut");
        }}
        initialRange={customRange ?? value}
        minDate={minDate}
        maxDate={maxDate}
        limitDays={limitDays}
      />
    </>
  );
};
